// Textos do cartão da lista de descontos e da confirmação de "Desativar". Funções puras: recebem o
// registro da lista (GET /discounts) e os nomes já carregados (postos, combustíveis, etiquetas).
//
// O que a lista NÃO traz: nome dos postos/combustíveis/etiquetas (só ids) e a quantidade de
// abastecimentos. Da lista de clientes (`customer_discount`) vem só `hasCustomers`. Backend anterior
// ao #431 não manda o campo: aí a promoção sem data e sem etiqueta/grupo/cidade vira "Lista de
// clientes", porque o cadastro só deixa salvar promoção aberta a todos se ela tiver data para acabar.
import { formatReal } from '../../../lib/formatReal'
import { __t } from '../../../i18n/t'
import { DispatchMessageStatus } from '../../../models/dispatchMessageStatus'
import { FuelDiscountCostValueType } from '../../../models/fuelDiscountCostValueType'
import {
  DiscountLike, DiscountStatus, DiscountStatusKey, DiscountTypeKey, clockStr, dayLabel, fmtN, hasScheduleWindows, listText, scheduleQualifier, typeKey, typeLabel,
} from './discountStatus'

export interface NamedItem {
  id: string
  name: string
}

/** Nomes já carregados. Mapa vazio = sem permissão ou sem dado: o texto cai na contagem. */
export interface Lookups {
  stations: NamedItem[]
  fuels: Map<string, string>
  tags: Map<string, string>
  groups: Map<string, string>
  /** Ainda buscando algum dos nomes: o cartão mostra um esqueleto em vez de uma contagem que vai mudar. */
  loading: boolean
}

export interface FuelOption {
  fuelId?: string | null
  alterPriceValue?: number | string | null
  costValueType?: string | null
  costValue?: number | string | null
}

export interface SummaryInput extends DiscountLike {
  identify?: string | null
  description?: string | null
  placeIds?: string[] | null
  placeDiscounts?: Array<{ placeId: string }> | null
  customerTagIds?: string[] | null
  customerGroupIds?: string[] | null
  cityIds?: string[] | null
  fuelDiscounts?: FuelOption[] | null
  /** Tem lista de clientes (customer_discount). Vem da lista desde o backend #431; antes, undefined. */
  hasCustomers?: boolean | null
}

const uniq = <T>(items: T[]) => Array.from(new Set(items))

const namesOf = (ids: string[], names: Map<string, string>) =>
  ids.map(id => names.get(id)).filter((name): name is string => Boolean(name))

// ─── Valor ──────────────────────────────────────────────────────────────────────

export interface ValueSummary {
  /** "R$ 0,20 de desconto por litro" */
  headline: string
  /** "Etanol, Gasolina Comum" */
  fuels: string
  /** "ou R$ 0,40 gastando 100 pontos por abastecimento" */
  extra: string
  /** false = nenhuma opção com valor maior que zero: o cliente não vê este desconto. */
  hasValue: boolean
}

export const valueSummary = (d: SummaryInput, fuelNames: Map<string, string>): ValueSummary => {
  // Recompensa nunca cobra pontos na bomba (custo forçado a 0).
  const reward = typeKey(d) === DiscountTypeKey.reward
  const options = (d.fuelDiscounts || [])
    .map(f => ({
      fuelId: f.fuelId || '',
      value: Number(f.alterPriceValue) || 0,
      points: !reward && (f.costValueType ?? FuelDiscountCostValueType.points) === FuelDiscountCostValueType.points ? Number(f.costValue) || 0 : 0,
    }))
    .filter(o => o.value > 0)
  if (!options.length) return { headline: '', fuels: '', extra: '', hasValue: false }

  const free = options.filter(o => o.points <= 0)
  const paid = options.filter(o => o.points > 0).sort((a, b) => b.value - a.value || a.points - b.points)
  const main = free.length ? free : paid
  const max = Math.max(...main.map(o => o.value))
  const headline = main.every(o => o.value === max)
    ? __t('%{valor} de desconto por litro', { valor: formatReal(max) })
    : __t('até %{valor} de desconto por litro', { valor: formatReal(max) })

  let extra = ''
  if (!free.length) {
    extra = paid[0].points === 1
      ? __t('gastando 1 ponto por abastecimento')
      : __t('gastando %{pontos} pontos por abastecimento', { pontos: fmtN(paid[0].points) })
  }
  else if (paid.length && paid[0].value > max) {
    extra = paid[0].points === 1
      ? __t('ou %{valor} gastando 1 ponto por abastecimento', { valor: formatReal(paid[0].value) })
      : __t('ou %{valor} gastando %{pontos} pontos por abastecimento', { valor: formatReal(paid[0].value), pontos: fmtN(paid[0].points) })
  }

  const fuelIds = uniq(options.map(o => o.fuelId).filter(Boolean))
  const names = namesOf(fuelIds, fuelNames)
  let fuels: string
  if (names.length && names.length === fuelIds.length && fuelIds.length <= 3) fuels = names.join(', ')
  else fuels = fuelIds.length === 1 ? __t('1 combustível') : __t('%{n} combustíveis', { n: fuelIds.length })
  return { headline, fuels, extra, hasValue: true }
}

// ─── Postos ─────────────────────────────────────────────────────────────────────

export const stationIdsOf = (d: SummaryInput): string[] =>
  d.placeIds?.length ? uniq(d.placeIds) : uniq((d.placeDiscounts || []).map(p => p.placeId).filter(Boolean))

const stationInfo = (d: SummaryInput, stations: NamedItem[]) => {
  const ids = stationIdsOf(d)
  const byId = new Map(stations.map(s => [s.id, s.name]))
  return {
    ids,
    names: namesOf(ids, byId),
    // "Todos" só quando a rede tem mais de um posto e o desconto cobre todos eles.
    all: stations.length > 1 && stations.every(s => ids.includes(s.id)),
  }
}

export const stationsText = (d: SummaryInput, stations: NamedItem[]): string => {
  const { ids, names, all } = stationInfo(d, stations)
  if (all) return __t('Todos os %{n} postos', { n: stations.length })
  if (!ids.length) return __t('Nenhum posto')
  if (names.length === ids.length && ids.length <= 2) return listText(names)
  return ids.length === 1 ? __t('1 posto') : __t('%{n} postos', { n: ids.length })
}

// ─── Título ─────────────────────────────────────────────────────────────────────

/** Padrão não tem nome (o backend descarta): o cartão o chama pelos postos. */
export const cardTitle = (d: SummaryInput, stations: NamedItem[]): string => {
  const name = (d.description || '').trim()
  if (name) return name
  const kind = typeKey(d)
  if (kind === DiscountTypeKey.default) {
    const { ids, names, all } = stationInfo(d, stations)
    if (all) return __t('Padrão de todos os postos')
    if (names.length && names.length === ids.length && ids.length <= 2) return __t('Padrão de %{postos}', { postos: listText(names) })
    return ids.length === 1 ? __t('Padrão de 1 posto') : __t('Padrão de %{n} postos', { n: ids.length })
  }
  return __t('%{tipo} %{codigo}', { tipo: typeLabel(kind), codigo: d.identify || '' }).trim()
}

// ─── Público ────────────────────────────────────────────────────────────────────

const namedOrCount = (ids: string[], names: Map<string, string>, one: (nome: string) => string, many: (nomes: string) => string, count: (n: number) => string): string => {
  if (!ids.length) return ''
  const found = namesOf(ids, names)
  if (found.length === ids.length && ids.length <= 2) return found.length === 1 ? one(found[0]) : many(listText(found))
  return count(ids.length)
}

export const audienceText = (d: SummaryInput, lookups: Pick<Lookups, 'tags' | 'groups'>): string => {
  switch (typeKey(d)) {
    case DiscountTypeKey.default: return __t('Todos os clientes do app')
    case DiscountTypeKey.reward: return __t('Quem conquista o brinde no Clube de Vantagens')
    case DiscountTypeKey.recovery: return __t('Quem recebe o cupom da campanha')
    default: break
  }
  // Lista de clientes trava o público: só quem está nela, com ou sem etiqueta.
  if (d.hasCustomers) return __t('Lista de clientes')

  const tags = namedOrCount(
    d.customerTagIds || [], lookups.tags,
    nome => __t('Etiqueta %{nome}', { nome }),
    nomes => __t('Etiquetas %{nomes}', { nomes }),
    n => (n === 1 ? __t('1 etiqueta') : __t('%{n} etiquetas', { n })),
  )
  const groups = namedOrCount(
    d.customerGroupIds || [], lookups.groups,
    nome => __t('Grupo %{nome}', { nome }),
    nomes => __t('Grupos %{nomes}', { nomes }),
    n => (n === 1 ? __t('1 grupo') : __t('%{n} grupos', { n })),
  )
  const cityCount = (d.cityIds || []).length
  // Etiqueta OU grupo; a cidade restringe os dois (cidade E (grupo OU etiqueta)).
  const who = listText([tags, groups].filter(Boolean), 'disjunction')

  if (who) {
    if (!cityCount) return who
    return `${who} · ${cityCount === 1 ? __t('só em 1 cidade') : __t('só em %{n} cidades', { n: cityCount })}`
  }
  if (cityCount) return cityCount === 1 ? __t('Clientes de 1 cidade') : __t('Clientes de %{n} cidades', { n: cityCount })
  if (d.startAt || d.endAt || d.hasCustomers === false) return __t('Todos os clientes do app')
  if ((d.fuelDiscounts || []).some(f => f.costValueType === FuelDiscountCostValueType.dotz)) return __t('Clientes que trocam Dotz')
  return __t('Lista de clientes')
}

// ─── Período ────────────────────────────────────────────────────────────────────

/** null = o tipo não tem período (Padrão, Recompensa, Recuperação valem sempre). */
export const periodText = (d: SummaryInput, now: Date = new Date()): string | null => {
  const start = dayLabel(d.startAt, now)
  const end = dayLabel(d.endAt, now)
  let text: string
  if (start && end) text = start === end ? start : __t('%{inicio} a %{fim}', { inicio: start, fim: end })
  else if (start) text = __t('A partir de %{dia}', { dia: start })
  else if (end) text = __t('Até %{dia}', { dia: end })
  else return typeKey(d) === DiscountTypeKey.special ? __t('Sem data para acabar') : null

  const qualifier = scheduleQualifier(d)
  return qualifier ? `${text} · ${qualifier}` : text
}

// ─── Avisos do cartão ───────────────────────────────────────────────────────────

export enum NoteKind {
  info = 'info',
  lock = 'lock',
  history = 'history',
  warning = 'warning',
}

export interface CardNote {
  kind: NoteKind
  text: string
}

export const cardNotes = (d: SummaryInput, status: DiscountStatus, hasValue: boolean): CardNote[] => {
  const notes: CardNote[] = []
  const running = status.key === DiscountStatusKey.active || status.key === DiscountStatusKey.scheduled
  if (status.key === DiscountStatusKey.disabled && typeKey(d) === DiscountTypeKey.default && !d.disabledAt) {
    notes.push({ kind: NoteKind.history, text: __t('Um Padrão mais novo assumiu o lugar deste.') })
  }
  if (running && !hasValue) {
    notes.push({ kind: NoteKind.warning, text: __t('Nenhuma opção tem valor maior que zero, então os clientes não veem este desconto.') })
  }
  // Semanal ou diário sem nenhum dia gravado (aconteceu em cadastros antigos): a bomba nunca o aplica.
  if (running && hasScheduleWindows(d) && Array.isArray(d.dayScheduledDiscounts) && d.dayScheduledDiscounts.length === 0) {
    notes.push({ kind: NoteKind.warning, text: __t('Não há dias nem horários marcados, então este desconto nunca vale para o cliente.') })
  }
  // A lista não sabe se a campanha está rodando; o backend recusa com 409 e diz qual é.
  if (running && typeKey(d) === DiscountTypeKey.recovery) {
    notes.push({ kind: NoteKind.lock, text: __t('Se uma campanha de recuperação em andamento usa este desconto, não dá para desativar. Para parar, encerre a campanha.') })
  }
  return notes
}

// ─── Desativar ──────────────────────────────────────────────────────────────────

export interface DisableCopy {
  title: string
  text: string
  toast: string
}

export const disableCopy = (d: SummaryInput, status: DiscountStatus, name: string): DisableCopy => {
  const codigo = d.identify || ''
  const title = __t('Desativar %{codigo}?', { codigo })
  if (typeKey(d) === DiscountTypeKey.reward) {
    return {
      title,
      text: __t('“%{nome}” deixa de aparecer na lista ao criar novos brindes no Clube de Vantagens. Quem já conquistou o brinde continua com o desconto. Para parar de dar o desconto, desative o brinde no Clube de Vantagens.', { nome: name }),
      toast: __t('%{codigo} desativado. Os brindes que já usam este desconto continuam valendo.', { codigo }),
    }
  }
  if (status.key === DiscountStatusKey.scheduled) {
    return {
      title,
      text: __t('“%{nome}” ainda não começou. Ao desativar, ele não vai começar e passa para “Desativados”.', { nome: name }),
      toast: __t('%{codigo} desativado. Ele não vai começar.', { codigo }),
    }
  }
  if (typeKey(d) === DiscountTypeKey.default) {
    return {
      title,
      text: __t('“%{nome}” deixa de valer. Nesses postos, os clientes ficam sem o preço do app até você criar outro Padrão.', { nome: name }),
      toast: __t('%{codigo} desativado.', { codigo }),
    }
  }
  return {
    title,
    text: __t('“%{nome}” deixa de aparecer para os clientes. Quem já começou um abastecimento com ele termina normalmente.', { nome: name }),
    toast: __t('%{codigo} desativado.', { codigo }),
  }
}

// ─── Notificação do desconto ao desativar ───────────────────────────────────────
// Desativar cancela no backend, na mesma transação, todo disparo da notificação que ainda está
// 'scheduled' (PATCH /discounts/{id} devolve `canceledNotifications`). Disparo 'sending' ou enviado
// não volta: a confirmação avisa que os clientes já podem ter recebido a oferta.

export enum DisableNotificationKind {
  /** Ainda não saiu: desativar também cancela. */
  pending = 'pending',
  /** Começou a sair: não dá mais para cancelar. */
  sending = 'sending',
  /** Já foi enviada aos clientes. */
  sent = 'sent',
}

export interface DisableNotification {
  kind: DisableNotificationKind
  title: string
  /** Data de envio do disparo pendente; não vem nos outros casos. */
  sendAt?: string | Date | null
}

interface DispatchLike { status?: string | null, sendAt?: string | Date | null }
interface CampaignLike { title?: string | null, dispatchMessages?: DispatchLike[] | null }

const SENT_STATUSES: string[] = [DispatchMessageStatus.dispatched, DispatchMessageStatus.dispatchedWithError]

/**
 * O que a confirmação de "Desativar" diz da notificação (GET /discounts/{id}). Pendente vence enviando, que
 * vence enviada: com push e SMS, o canal que ainda não saiu é o que o desativar muda. Cancelada, com falha
 * ou sem notificação: null.
 */
export const disableNotification = (record: { discountMarketingCampaigns?: CampaignLike[] | null } | null | undefined): DisableNotification | null => {
  const campaigns = record?.discountMarketingCampaigns || []
  const find = (statuses: string[]) => {
    for (const campaign of campaigns) {
      const dispatch = (campaign.dispatchMessages || []).find(m => statuses.includes(m.status || ''))
      if (dispatch) return { title: campaign.title || '', dispatch }
    }
    return null
  }
  const pending = find([DispatchMessageStatus.scheduled])
  if (pending) return { kind: DisableNotificationKind.pending, title: pending.title, sendAt: pending.dispatch.sendAt ?? null }
  const sending = find([DispatchMessageStatus.sending])
  if (sending) return { kind: DisableNotificationKind.sending, title: sending.title }
  const sent = find(SENT_STATUSES)
  if (sent) return { kind: DisableNotificationKind.sent, title: sent.title }
  return null
}

export interface DisableNotificationCopy {
  text: string
  severity: 'info' | 'warning'
  /** Botão de confirmar. */
  confirm: string
}

export const disableNotificationCopy = (n: DisableNotification, now: Date = new Date()): DisableNotificationCopy => {
  const titulo = n.title
  if (n.kind === DisableNotificationKind.pending) {
    const sendAt = n.sendAt ? new Date(n.sendAt) : null
    const future = sendAt && !Number.isNaN(sendAt.getTime()) && sendAt.getTime() > now.getTime()
    return {
      text: future
        ? __t('A notificação “%{titulo}”, agendada para %{dia} às %{hora}, também será cancelada.', { titulo, dia: dayLabel(sendAt, now), hora: clockStr(sendAt) })
        : __t('A notificação “%{titulo}” ainda não saiu e também será cancelada.', { titulo }),
      severity: 'info',
      confirm: __t('Desativar e cancelar notificação'),
    }
  }
  return {
    text: n.kind === DisableNotificationKind.sending
      ? __t('A notificação “%{titulo}” já está sendo enviada e não dá mais para cancelar. Quem receber pode tentar usar a promoção, que não vai mais valer.', { titulo })
      : __t('A notificação “%{titulo}” já foi enviada aos clientes. Quem recebeu pode tentar usar a promoção, que não vai mais valer.', { titulo }),
    severity: 'warning',
    confirm: __t('Desativar mesmo assim'),
  }
}

/**
 * Aviso depois de desativar. `canceled` é o `canceledNotifications` do PATCH: backend anterior a esta
 * mudança não manda o campo, e aí o aviso não promete nada sobre a notificação.
 */
export const disableToast = (toast: string, notification: DisableNotification | null, canceled: unknown): string => {
  if (typeof canceled !== 'number') return toast
  if (canceled > 0) return `${toast} ${__t('A notificação foi cancelada.')}`
  // A tela viu a notificação pendente, mas o envio começou antes do clique: ela já está saindo.
  if (notification?.kind === DisableNotificationKind.pending) return `${toast} ${__t('A notificação já tinha começado a sair e não foi cancelada.')}`
  return toast
}

// O backend recusa com 409 e a mensagem "Desconto em uso na campanha {nome}": vira frase inteira,
// com o nome da campanha, para o dono saber o que encerrar. Outro 4xx traz uma frase em português do backend, que é
// chave do catálogo (passa por __t). Erro 5xx ou rede caída ("An internal server error occurred", "Failed to fetch")
// não é para o dono ler: vira uma frase simples.
const CAMPAIGN_IN_USE = /^Desconto em uso na campanha (.+)$/

export const disableErrorText = (error: { message?: unknown; status?: unknown } | null | undefined): string => {
  const message = typeof error?.message === 'string' ? error.message : ''
  const campaign = CAMPAIGN_IN_USE.exec(message)
  if (campaign) return __t('Este desconto está em uso na campanha “%{nome}”. Para desativar, encerre a campanha antes.', { nome: campaign[1] })
  const status = Number(error?.status)
  if (message && status >= 400 && status < 500) return __t(message)
  return __t('Não foi possível desativar. Tente de novo.')
}
